import { TransferSources } from '@interfaces/transfer';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';

import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { Popover } from '@ovComponents/2-component/popover/popover';
import { ConfirmationModal } from '@ovComponents/3-pattern/confirmationDialog/confirmationDialog';
import { CopyId } from '@ovComponents/3-pattern/copyId/copyId';
import { ExternalTransferInManualProcessInfo } from '@ovComponents/3-pattern/externalTransferInManualProcessInfo/externalTransferInManualProcessInfo';
import { StateStepper } from '@ovComponents/3-pattern/stateStepper/stateStepper';
import { SuspiciousActivityAccordion } from '@ovComponents/3-pattern/suspiciousActivityAccordion/suspiciousActivityAccordion';
import { ValueSuperscriptedCents } from '@ovComponents/3-pattern/valueSuperscriptedCents/valueSuperscriptedCents';
import { usePermission } from '@providers/userContextProvider';
import { generateTransferSourceText } from '@utils/common';
import { useLocalization } from '@utils/useLocalization';
import { Box, Button, IconButton, Typography, useTheme } from 'hephaestus/web';
import { Close as CloseIcon, ExpandMore } from 'hephaestus/webIcons';
import { isNull } from 'lodash-es';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

enum TransferStates {
  REVIEWING = 'REVIEWING',
  INITIATED = 'INITIATED',
  READY = 'READY',
  REQUESTED = 'REQUESTED',
  PROCESSING = 'PROCESSING',
  RECONCILED = 'RECONCILED',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED',
  ACTIVE = 'ACTIVE',
}

const CORE_STATES = [
  { name: TransferStates.INITIATED },
  { name: TransferStates.READY },
  { name: TransferStates.REQUESTED },
  { name: TransferStates.PROCESSING },
  { name: TransferStates.RECONCILED },
];

const activeStepFinder = (state: string) => {
  switch (state) {
    case TransferStates.INITIATED:
      return 0;
    case TransferStates.READY:
      return 1;
    case TransferStates.REQUESTED:
      return 2;
    case TransferStates.PROCESSING:
      return 3;
    case TransferStates.RECONCILED:
      return 4;
    default:
      return null;
  }
};

type StateToTransition = {
  name?: string;
  from?: TransferStates[];
  error?: boolean;
  show?: boolean;
  tonal?: boolean;
};

export type TransferModalOptions = {
  canTransition?: boolean;
  canCancelPending?: boolean;
  canCancelScheduled?: boolean;
  canEditScheduled?: boolean;
  scheduledTransferWorkflow?: string;
};

export const TransferModal = ({
  transfer,
  handleClose,
  open,
  options,
  transition,
  showCopyId = false,
  showStatePopover = false,
  showSuspiciousActivity = false,
  showBasicInfo = true,
  handleEditScheduled,
}: {
  transfer: any;
  handleClose: any;
  open: any;
  options: TransferModalOptions;
  showCopyId?: boolean;
  showStatePopover?: boolean;
  showSuspiciousActivity?: boolean;
  showBasicInfo?: boolean;
  transition: (
    transferId: string,
    isScheduled: boolean,
    variables: any,
    transfer?: boolean,
  ) => void;
  handleEditScheduled?: () => void;
}) => {
  const { t } = useTranslation(['components', 'shared']);
  const [activeStep, setActiveStep] = useState(0);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [stateToTransition, setStateToTransition] = useState<StateToTransition>(
    {},
  );
  const [statePopoverAnchorEl, setStatePopoverAnchorEl] =
    useState<HTMLElement | null>(null);
  const theme = useTheme();
  const statePopoverOpen = Boolean(statePopoverAnchorEl);
  const canTransitionReconcileCanceledTransfer = usePermission(
    'transition:reconcile_canceled_transfer',
  );
  const canTransitionTransfer = usePermission('transition:transfer');
  const { localizedDate } = useLocalization();

  useEffect(() => {
    if (transfer && transfer.stateMachineChanges) {
      let step = activeStepFinder(transfer.state);
      if (isNull(step)) {
        step = activeStepFinder(
          transfer.stateMachineChanges.slice(-1)[0]?.from,
        );
      }
      setActiveStep(step || 0);
    }
  }, [transfer]);

  const transitions = [
    {
      name: 'review',
      from: [
        TransferStates.INITIATED,
        TransferStates.READY,
        TransferStates.REQUESTED,
        TransferStates.PROCESSING,
      ],

      error: false,
      show: options.canTransition,
      tonal: true,
    },
    {
      name: 'initiate',
      from: [TransferStates.REVIEWING],
      error: false,
      show: options.canTransition,
    },
    {
      name: 'ready',
      from: [TransferStates.INITIATED, TransferStates.REVIEWING],
      error: false,
      show: options.canTransition,
    },
    {
      name: 'request',
      from: [TransferStates.READY, TransferStates.REVIEWING],
      error: false,
      show: options.canTransition,
    },
    {
      name: 'process',
      from: [TransferStates.REQUESTED, TransferStates.REVIEWING],
      error: false,
      show: options.canTransition,
    },
    {
      name: 'reconcile',
      from: [TransferStates.REQUESTED, TransferStates.PROCESSING],
      error: false,
      show: options.canTransition,
    },
    {
      name: 'fail',
      from: [TransferStates.PROCESSING, TransferStates.REQUESTED],
      error: true,
      show: options.canTransition && options.canCancelPending,
    },
    {
      name: 'cancelReconciled',
      from: [TransferStates.RECONCILED],
      error: true,
      show: options.canTransition && options.canCancelPending,
    },
    {
      name: 'cancel',
      from: [
        TransferStates.INITIATED,
        TransferStates.READY,
        TransferStates.REVIEWING,
        TransferStates.PROCESSING,
      ],

      error: true,
      show: options.canCancelPending,
    },
    {
      name: 'cancelScheduled',
      from: [TransferStates.ACTIVE],
      error: true,
      show: options.canCancelScheduled,
    },
    {
      name: 'flipCancelToReconcile',
      from: [TransferStates.CANCELED],
      error: false,
      show: canTransitionReconcileCanceledTransfer,
    },
  ];

  const onTransitionClick = (tr: StateToTransition) => {
    transition(
      transfer.id,
      transfer.state === TransferStates.ACTIVE,
      { transition: tr.name },
      transfer,
    );
    handleClose();
  };

  if (!transfer?.state) {
    return <></>;
  }

  const infoTextVariant = showBasicInfo ? 'bodySmall' : 'bodyMedium';
  const transitionOptions = transitions.filter(
    (tr) => tr.from.includes(transfer.state) && !tr.error && tr.show,
  );

  const transitionConfirmationDialog = (tr: StateToTransition) => {
    if (tr.name === 'cancelScheduled' || tr.name === 'cancel') {
      setStateToTransition(tr);
      setDeleteConfirmationOpen(true);
    } else {
      onTransitionClick(tr);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        sx={{ '& .MuiDialog-paper': { maxWidth: '640px' } }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {t('components:transfers.transferDetails')}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'end',
                alignItems: 'center',
              }}
            >
              {showCopyId && <CopyId id={transfer.id} />}
              <Box
                sx={{
                  ml: 1,
                }}
              />
              <IconButton
                aria-label='close'
                onClick={handleClose}
                data-testid='transfer-modal-close-button'
              >
                <CloseIcon fontSize='inherit' />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: showBasicInfo ? 3 : 0 }}>
          <Box
            sx={{
              px: showBasicInfo ? 0 : 3,
            }}
          >
            {showSuspiciousActivity && (
              <Box
                sx={{
                  py: 1,
                }}
              >
                <SuspiciousActivityAccordion transfer={transfer} />
              </Box>
            )}
            {transfer.source === TransferSources.EXTERNAL_TRANSFER_IN && (
              <ExternalTransferInManualProcessInfo transfer={transfer} />
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: showBasicInfo ? 'space-between' : 'flex-end',
                gap: 4,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: showBasicInfo ? '2px' : 0,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                  }}
                >
                  <ValueSuperscriptedCents
                    value={transfer.amountCents}
                    mainVariant='displayLarge'
                    centsVariant='headingLarge'
                  />
                </Box>
                <Typography
                  variant={infoTextVariant}
                  color={showBasicInfo ? 'textSecondary' : undefined}
                >
                  {t(`pendingTransfers.types.${transfer.type}`)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: showBasicInfo ? '250px' : '100%',
                }}
              >
                {(transfer.source ||
                  !!(transfer.toSubAccount || transfer.bankAccount)) && (
                  <Box sx={{ ...basicInfoSx, py: showBasicInfo ? 0 : 1 }}>
                    <Typography variant={infoTextVariant}>
                      {t('components:transfers.source')}
                    </Typography>
                    <Typography
                      variant={infoTextVariant}
                      sx={{ fontWeight: 600 }}
                    >
                      {transfer.source
                        ? transfer.source === 'USER' &&
                          transfer.bankAccount.name
                          ? `${transfer.bankAccount.name} (${transfer.bankAccount.bankAccountNumber})`
                          : t(`components:transferSources.${transfer.source}`)
                        : generateTransferSourceText(t, transfer)}
                    </Typography>
                  </Box>
                )}
                <Box
                  sx={{
                    ...basicInfoSx,
                    ...(!showBasicInfo && {
                      borderTop: `1px solid ${theme.color.outlineVariant}`,
                      py: 1,
                    }),
                  }}
                >
                  <Typography variant={infoTextVariant}>
                    {t('components:transfers.date')}
                  </Typography>
                  <Typography
                    variant={infoTextVariant}
                    sx={{ fontWeight: 600 }}
                  >
                    {localizedDate(
                      transfer.scheduledDate
                        ? transfer.scheduledDate
                        : transfer.date,
                    )}
                  </Typography>
                </Box>
                {transfer.frequency && (
                  <Box
                    sx={{
                      ...basicInfoSx,
                      ...(!showBasicInfo && {
                        borderTop: `1px solid ${theme.color.outlineVariant}`,
                        py: 1,
                      }),
                    }}
                  >
                    <Typography variant={infoTextVariant}>
                      {t('components:transfers.frequency')}
                    </Typography>
                    <Typography
                      variant={infoTextVariant}
                      sx={{ fontWeight: 600 }}
                    >
                      {t(
                        `components:transfersTable.frequencyOption.${transfer.frequency}`,
                      )}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          {transfer.stateMachineChanges && (
            <Box
              sx={{
                borderTop: `1px solid ${theme.color.outlineVariant}`,
                pt: 2,
                px: showBasicInfo ? 0 : 3,
                mt: 2,
              }}
            >
              <Typography variant='bodyMedium' sx={{ fontWeight: 600, mb: 2 }}>
                {t('components:transfers.transferProgress')}
              </Typography>
              <StateStepper
                steps={CORE_STATES}
                failed={[
                  TransferStates.CANCELED,
                  TransferStates.FAILED,
                  TransferStates.REVIEWING,
                ].includes(transfer.state)}
                activeStep={activeStep}
                currentState={transfer.state}
                stateChanges={[
                  {
                    to: TransferStates.INITIATED,
                    createdAt: transfer.createdAt,
                  },
                  ...transfer.stateMachineChanges,
                ]}
              />
            </Box>
          )}
        </DialogContent>
        {canTransitionTransfer &&
          transitions.filter(
            (tr) => tr.from.includes(transfer.state) && tr.show,
          ).length > 0 && (
            <DialogFooter>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                  }}
                >
                  {transitions
                    .filter(
                      (tr) =>
                        tr.from.includes(transfer.state) && tr.error && tr.show,
                    )
                    .map((tr) => (
                      <Button
                        key={tr.name}
                        variant='contained'
                        color='error'
                        data-testid={`transfer-modal-destructive-button-${tr.name}`}
                        sx={{ mr: 1 }}
                        onClick={() => transitionConfirmationDialog(tr)}
                      >
                        {t(`transferTransitions.${tr.name}`)}
                      </Button>
                    ))}
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                  }}
                >
                  {showStatePopover
                    ? transitionOptions.length > 0 && (
                        <>
                          <Button
                            variant='outlined'
                            endIcon={<ExpandMore />}
                            onClick={(e: React.MouseEvent<HTMLElement>) =>
                              setStatePopoverAnchorEl(e.currentTarget)
                            }
                          >
                            {t('components:transfers.setState')}
                          </Button>
                          <Popover
                            open={statePopoverOpen}
                            anchorEl={statePopoverAnchorEl}
                            onClose={() => setStatePopoverAnchorEl(null)}
                            anchorOrigin={{
                              vertical: 'top',
                              horizontal: 'left',
                            }}
                            transformOrigin={{
                              vertical: 'bottom',
                              horizontal: 'left',
                            }}
                            sx={{
                              '& .MuiPaper-root': {
                                minWidth: 140,
                                borderRadius: theme.borderRadius.md,
                                mt: -1,
                              },
                            }}
                          >
                            {transitionOptions.map((tr) => {
                              const label = t(`transferTransitions.${tr.name}`);
                              return (
                                <MenuItem
                                  key={tr.name}
                                  value={label}
                                  sx={{ px: 2, py: 1 }}
                                  data-testid='transfer-modal-state-popover-button'
                                  onClick={() => {
                                    onTransitionClick(tr);
                                    setStatePopoverAnchorEl(null);
                                  }}
                                >
                                  {label}
                                </MenuItem>
                              );
                            })}
                          </Popover>
                        </>
                      )
                    : transitionOptions.map((tr) => (
                        <Button
                          key={tr.name}
                          variant='contained'
                          color={tr.tonal ? 'secondary' : 'primary'}
                          sx={{ ml: 1 }}
                          data-testid={`transfer-modal-transition-button-${tr.name}`}
                          onClick={() => onTransitionClick(tr)}
                        >
                          {t(`transferTransitions.${tr.name}`)}
                        </Button>
                      ))}
                  {options.canEditScheduled &&
                    transfer.frequency &&
                    transfer.scheduledDate && (
                      <Button
                        variant='contained'
                        onClick={() => {
                          handleEditScheduled?.();
                          handleClose();
                        }}
                      >
                        {t('transferTransitions.editScheduled')}
                      </Button>
                    )}
                </Box>
              </Box>
            </DialogFooter>
          )}
      </Dialog>
      <ConfirmationModal
        open={deleteConfirmationOpen}
        onCancel={() => setDeleteConfirmationOpen(false)}
        onConfirm={() => {
          transition(
            transfer.id,
            transfer.state === TransferStates.ACTIVE,
            { transition: stateToTransition?.name },
            transfer,
          );
          setDeleteConfirmationOpen(false);
          handleClose();
        }}
        title={t(`transferTransitions.${stateToTransition?.name}`)}
        bodyText={t(`transfers.confirmationDialog.${stateToTransition?.name}`)}
        cancelButtonLabel={t('transfers.cancel')}
        confirmButtonLabel={t('transfers.confirm')}
        cancelButtonColor='error'
        cancelButtonVariant='contained'
        maxWidth='xs'
        testIdPrefix='transfer-modal-confirmation-modal'
        dialogStyles={{
          dialogContainer: {
            '& .MuiDialogActions-root': {
              borderTop: 'none !important',
              justifyContent: 'center!important',
            },
            '& .MuiDialogTitle-root': {
              borderBottom: 'none !important',
              fontSize: '24px !important',
            },
            '& .MuiDialogContent-root': {
              paddingTop: '0px !important',
            },
          },
        }}
      />
    </>
  );
};

const basicInfoSx = {
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
};
